import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  Database,
  FileText,
  Gauge,
  Layers3,
  MessageSquare,
  Play,
  RefreshCcw,
  Search,
  ShieldCheck,
  Sparkles,
  Upload,
  Wand2,
  Zap
} from "lucide-react";
import { cases, getCase, type CaseDemo } from "./cases";
import "./styles.css";

const iconMap = [MessageSquare, Search, Database, FileText, Gauge, Wand2, Bot, Upload, Layers3, Zap];

type AiResult = {
  ok: boolean;
  provider?: string;
  model?: string;
  text: string;
  fallback?: boolean;
};

function casePath(id: string) {
  return `#/${id}`;
}

function useHashCase() {
  const initial = location.hash.replace(/^#\/?/, "") || cases[0].id;
  const [activeId, setActiveId] = useState(getCase(initial).id);

  function selectCase(id: string) {
    const next = getCase(id).id;
    history.pushState(null, "", casePath(next));
    setActiveId(next);
  }

  React.useEffect(() => {
    const onHash = () => setActiveId(getCase(location.hash.replace(/^#\/?/, "")).id);
    addEventListener("hashchange", onHash);
    return () => removeEventListener("hashchange", onHash);
  }, []);

  return { activeCase: getCase(activeId), selectCase };
}

function Shell() {
  const { activeCase, selectCase } = useHashCase();
  const [query, setQuery] = useState(activeCase.sampleInput);
  const [result, setResult] = useState<AiResult | null>(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    setQuery(activeCase.sampleInput);
    setResult(null);
  }, [activeCase.id, activeCase.sampleInput]);

  const progress = useMemo(() => Math.round((activeCase.order / cases.length) * 100), [activeCase.order]);

  async function runDemo() {
    setLoading(true);
    setResult(null);
    try {
      const response = await fetch(`/api/ai/${activeCase.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: query })
      });
      const payload = (await response.json()) as AiResult;
      setResult(payload);
    } catch (error) {
      setResult({
        ok: false,
        fallback: true,
        text: `演示接口暂时不可用，但该案例的标准输出应包含：${activeCase.realAi.join("、")}。错误：${String(error)}`
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#/customer-service">
          <span>启</span>
          <strong>启序科技 AI 案例库</strong>
        </a>
        <nav>
          <a href="#cases">10 个案例</a>
          <a href="#demo">现场演示</a>
          <a href="#stack">技术底座</a>
        </nav>
        <a className="top-action" href="#demo">
          开始演示 <ArrowRight size={16} />
        </a>
      </header>

      <section className="hero">
        <div className="hero-copy">
          <h1>10 个能现场演示的 AI 行业案例网站。</h1>
          <p>
            用统一设计语言、统一 AI 调用层和可复用演示数据，展示启序科技从客服、RAG、内容生成到
            Agent 自动化的交付能力。
          </p>
          <div className="hero-actions">
            <a href="#cases" className="primary-button">
              查看案例 <Layers3 size={18} />
            </a>
            <button className="secondary-button" type="button" onClick={() => selectCase(activeCase.id)}>
              重置当前 Demo <RefreshCcw size={18} />
            </button>
          </div>
        </div>
        <div className="hero-panel">
          <div className="panel-head">
            <span>当前案例</span>
            <strong>{activeCase.order}/10</strong>
          </div>
          <h2>{activeCase.title}</h2>
          <p>{activeCase.promise}</p>
          <div className="progress-track">
            <span style={{ width: `${progress}%` }} />
          </div>
          <div className="mini-grid">
            {activeCase.metrics.map((metric) => (
              <div key={metric.label}>
                <strong>{metric.value}</strong>
                <span>{metric.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="case-layout" id="cases">
        <aside className="case-nav">
          {cases.map((item, index) => {
            const Icon = iconMap[index] ?? Sparkles;
            return (
              <button
                className={item.id === activeCase.id ? "active" : ""}
                key={item.id}
                type="button"
                onClick={() => selectCase(item.id)}
              >
                <Icon size={17} />
                <span>{String(item.order).padStart(2, "0")}</span>
                <strong>{item.shortTitle}</strong>
              </button>
            );
          })}
        </aside>

        <section className="case-main">
          <div className="case-title">
            <div>
              <p>{activeCase.industry}</p>
              <h2>{activeCase.title}</h2>
            </div>
            <span className="case-badge">由启序科技构建 · 案例演示</span>
          </div>

          <div className="capability-grid">
            <article>
              <h3>真实 AI 能力</h3>
              {activeCase.realAi.map((item) => (
                <p key={item}>
                  <CheckCircle2 size={16} /> {item}
                </p>
              ))}
            </article>
            <article>
              <h3>高保真 Mock</h3>
              {activeCase.mock.map((item) => (
                <p key={item}>
                  <ShieldCheck size={16} /> {item}
                </p>
              ))}
            </article>
            <article>
              <h3>页面结构</h3>
              {activeCase.pages.map((item) => (
                <p key={item}>
                  <FileText size={16} /> {item}
                </p>
              ))}
            </article>
          </div>

          <section className="demo-card" id="demo">
            <div className="demo-input">
              <div className="section-kicker">现场演示</div>
              <h3>{activeCase.primaryAction}</h3>
              <label htmlFor="demo-input">{activeCase.promptLabel}</label>
              <textarea
                id="demo-input"
                value={query}
                placeholder={activeCase.placeholder}
                onChange={(event) => setQuery(event.target.value)}
              />
              <div className="demo-actions">
                <button className="primary-button" type="button" onClick={runDemo} disabled={loading}>
                  {loading ? "AI 处理中..." : activeCase.primaryAction} <Play size={17} />
                </button>
                <button className="secondary-button" type="button" onClick={() => setQuery(activeCase.sampleInput)}>
                  填入示例 <RefreshCcw size={17} />
                </button>
              </div>
            </div>
            <div className="demo-output">
              <div className="terminal-head">
                <span />
                <span />
                <span />
                <strong>{result?.provider ? `${result.provider}/${result.model}` : "OpenClaw / MiniMax"}</strong>
              </div>
              <pre>
                {result
                  ? result.text
                  : `等待输入演示任务。\n\n建议话术：\n${activeCase.demoScript.map((item, index) => `${index + 1}. ${item}`).join("\n")}`}
              </pre>
            </div>
          </section>

          <section className="artifact-section">
            <div>
              <div className="section-kicker">演示资产</div>
              <h3>打开即能讲，任何时候可重置。</h3>
            </div>
            <div className="artifact-grid">
              {activeCase.artifacts.map((artifact) => (
                <div className={`artifact ${artifact.tone ?? "green"}`} key={artifact.label}>
                  <span>{artifact.label}</span>
                  <strong>{artifact.value}</strong>
                </div>
              ))}
            </div>
          </section>
        </section>
      </section>

      <section className="stack-section" id="stack">
        <div className="section-kicker">统一技术底座</div>
        <h2>同一套组件、同一个 AI 接口、同一种验收方式。</h2>
        <div className="stack-grid">
          <div>
            <Bot size={22} />
            <strong>OpenClaw + MiniMax</strong>
            <p>服务端统一调用模型，前端不暴露密钥。</p>
          </div>
          <div>
            <Database size={22} />
            <strong>演示数据内置</strong>
            <p>每个案例都有示例输入、页面结构和讲解话术。</p>
          </div>
          <div>
            <Gauge size={22} />
            <strong>上线可验证</strong>
            <p>页面、API、Nginx、TLS、服务进程都可独立验收。</p>
          </div>
        </div>
      </section>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <Shell />
  </React.StrictMode>
);
